import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { storeActivityIndicator } from '../../../../../redux/reducers/authSlice';
import CustomSingleSelect, { CustomSingleSelectModel } from '../../../../../components/CustomSingleSelect';
import { sayiYaz } from '../../../../../helpers/yakitHesap';
import { yakitTuruEslestir } from '../../../../../helpers/yakitAktarim';
import { YakitAktarimHatasi, YakitHataIslemSonucu, YakitSecenek, YakitSecenekleri, YakitService } from '../../../../../services/YakitService';

interface Props {
    hata: YakitAktarimHatasi | undefined
    onClose: () => void
    /** Düzeltme başarılı (fiş oluştu); liste yenilenir. */
    onDuzeltildi: () => void
}

/** Salt okunur bilgi satırı (etiket solda, değer sağda). */
const Bilgi = ({ etiket, deger }: { etiket: string, deger: React.ReactNode }) => (
    <div className="d-flex py-1 border-bottom border-gray-200">
        <div className="text-muted w-125px flex-shrink-0">{etiket}</div>
        <div className="fw-semibold text-gray-800">{deger ?? ''}</div>
    </div>
);

/**
 * Aktarım Hataları · Düzelt (04.11.2026, #547). Tek bekleyen hata satırı.
 * A1 Açılış: hata metni + kaynaktaki veri (tarih-saat, plaka, UTTS, ürün, litre, tutar, istasyon/açıklama) salt okunur;
 *    Araç boş (zorunlu), Yakıt Türü satırdaki ürün adıyla eşleşen seçenek önceden seçili (isteğe bağlı).
 * R1 "Düzelt" → araç seçilmemişse uyarı; seçiliyse Yakit/HataDuzelt { id, vehicleId, yakitTuruId }.
 *    duzeltilen > 0 → başarı toast, pencere kapanır, liste yenilenir; değilse dönen hatalar toast (pencere açık kalır).
 */
const HataDuzeltModal = ({ hata, onClose, onDuzeltildi }: Props) => {
    const dispatch = useDispatch();
    const [arac, setArac] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [yakitTuru, setYakitTuru] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [yakitTurleri, setYakitTurleri] = useState<YakitSecenek[]>([]);

    useEffect(() => {
        setArac(undefined);
        setYakitTuru(undefined);

        if (hata)
            yakitTurleriYukle(hata);
    }, [hata])

    const yakitTurleriYukle = async (h: YakitAktarimHatasi) => {
        try {
            var response = await YakitService.secenekler();
            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }
            const liste = (response.data as YakitSecenekleri).yakitTurleri ?? [];
            setYakitTurleri(liste);

            const eslesen = yakitTuruEslestir(h.yakitTuru, liste);
            setYakitTuru(eslesen ? { label: eslesen.label, value: eslesen.value } : undefined);
        } catch (error: any) {
            toast.error(error);
        }
    }

    const duzelt = async () => {
        if (!hata)
            return;
        if (!arac) {
            toast.error('Araç seçin.');
            return;
        }
        try {
            dispatch(storeActivityIndicator(true));
            var response = await YakitService.hataDuzelt(hata.id, arac.value, yakitTuru?.value ?? null);
            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }
            const s = response.data as YakitHataIslemSonucu;
            if (s.duzeltilen > 0) {
                toast.success('Kayıt düzeltildi; yakıt fişi oluşturuldu.');
                onDuzeltildi();
            }
            else
                toast.error(s.hatalar.length > 0 ? s.hatalar.join('; ') : 'Kayıt düzeltilemedi.', { duration: 6000 });
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    return (
        <Modal show={!!hata} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Aktarım hatasını düzelt</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {
                    hata && (
                        <React.Fragment>
                            <div className="alert alert-light-danger py-3 mb-5">{hata.hata}</div>

                            <h6 className="text-gray-800 mb-2">{hata.kaynakAdi} verisi</h6>
                            <div className="fs-7 mb-6">
                                <Bilgi etiket="Tarih - Saat" deger={[hata.islemTarihiStr, hata.islemSaati].filter(x => !!x).join(' ')} />
                                <Bilgi etiket="Plaka" deger={hata.plaka} />
                                <Bilgi etiket="UTTS No" deger={hata.uttsNo} />
                                <Bilgi etiket="Ürün" deger={hata.yakitTuru} />
                                <Bilgi etiket="Litre" deger={hata.miktar != null ? sayiYaz(hata.miktar) : ''} />
                                <Bilgi etiket="Tutar" deger={hata.brut != null ? sayiYaz(hata.brut) + ' TL' : ''} />
                                <Bilgi etiket="İstasyon" deger={hata.aciklama} />
                                {hata.hariciIslemNo && <Bilgi etiket="İşlem No" deger={hata.hariciIslemNo} />}
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <CustomSingleSelect
                                        labelText="Araç"
                                        isLabelRequired
                                        selectedValue={arac}
                                        onChange={setArac}
                                        onMenuOpen={() => YakitService.plakalar()}
                                        labelName='label'
                                        valueName='value'
                                        menuPortal
                                    />
                                </div>
                                <div className="col-md-6">
                                    <CustomSingleSelect
                                        labelText="Yakıt Türü"
                                        selectedValue={yakitTuru}
                                        onChange={setYakitTuru}
                                        onMenuOpen={() => Promise.resolve({ isSuccess: true, data: yakitTurleri })}
                                        labelName='label'
                                        valueName='value'
                                        menuPortal
                                    />
                                </div>
                            </div>
                            <div className="text-muted fs-7 mt-3">
                                Seçilen araca yakıt fişi oluşturulur. Aynı hata tekrar çıkmasın diye aracın kartındaki UTTS No / plaka bilgisini de kontrol edin.
                            </div>
                        </React.Fragment>
                    )
                }
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-sm btn-light" onClick={onClose}>Vazgeç</button>
                <button className="btn btn-sm btn-primary" onClick={duzelt}>Düzelt</button>
            </Modal.Footer>
        </Modal>
    )
}

export default HataDuzeltModal;
